<template>
    <div class="text-subtitle-2 font-weight-regular full-width">
        <div class="d-flex">
            <v-icon size="small" class="mr-1">{{ serviceTypeIcon }}</v-icon>
            <span class="font-weight-medium">
                {{ selectedGroupDetails.serviceName }}
            </span>
        </div>
        <v-row no-gutters>
            <v-col cols="12" md="6">
                <div class="d-flex align-center mb-4 text--secondary" :class="{'mt-3': isEditing.dateTime}">
                    <div v-if="isEditing.dateTime" class="d-flex align-center me-2">
                        <v-row dense>
                            <v-col cols="12" sm="6">
                                <date-picker
                                    v-model="startDate"
                                    outlined
                                    dense
                                    :label="$t('modify.subtitle_dates')"
                                    hide-details
                                />
                            </v-col>
                            <v-col cols="12" sm="6">
                                <time-field v-model="startTime" hide-details outlined dense />
                            </v-col>
                        </v-row>
                    </div>
                    <span v-else class="me-2">
                        {{ `${formattedDate}` }}
                    </span>
                    <template v-if="isTO1">
                        <v-btn
                            v-if="!isEditing.dateTime"
                            :loading="isLoading.dateTime"
                            :disabled="isLoading.dateTime"
                            color="primary"
                            icon="mdi-pencil"
                            variant="text"
                            size="x-small"
                            @click="startEditing('dateTime')"
                        />
                        <v-btn
                            v-if="isEditing.dateTime"
                            :loading="isLoading.dateTime"
                            :disabled="isLoading.dateTime"
                            color="error"
                            icon="mdi-close"
                            variant="text"
                            size="x-small"
                            @click="cancelEditing('dateTime')"
                        />
                        <v-btn
                            v-if="isEditing.dateTime"
                            :loading="isLoading.dateTime"
                            :disabled="isLoading.dateTime"
                            color="success"
                            icon="mdi-check"
                            variant="text"
                            size="x-small"
                            @click="saveDetailsInfo('dateTime')"
                        />
                    </template>
                </div>
            </v-col>
        </v-row>
        <div class="d-flex align-center info-item">
            <div class="mr-4 font-weight-medium nowrap" v-text="`${$t('status')}:`" />
            <v-select
                v-if="isEditing.status"
                v-model="status"
                :items="statusesList"
                :placeholder="$t('change_status')"
                :loading="isLoading.status"
                :disabled="isLoading.status"
                item-title="text"
                item-value="value"
                single-line
                density="compact"
                class="mt-0 mr-2 info-input"
                hide-details
            />
            <span v-else :class="statusColor" class="mr-2" v-text="orderStatus" />
            <template v-if="isTO1">
                <v-btn
                    v-if="!isEditing.status"
                    :loading="isLoading.status"
                    :disabled="isLoading.status"
                    color="primary"
                    icon="mdi-pencil"
                    variant="text"
                    size="x-small"
                    @click="startEditing('status')"
                />
                <v-btn
                    v-if="isEditing.status"
                    :loading="isLoading.status"
                    :disabled="isLoading.status"
                    color="error"
                    icon="mdi-close"
                    variant="text"
                    size="x-small"
                    @click="cancelEditing('status')"
                />
                <v-btn
                    v-if="isEditing.status"
                    :loading="isLoading.status"
                    :disabled="isLoading.status"
                    color="success"
                    icon="mdi-check"
                    variant="text"
                    size="x-small"
                    @click="saveDetailsInfo('status')"
                />
            </template>
        </div>
        <v-row no-gutters>
            <v-col cols="12" md="5">
                <div v-if="isTO1 || guide" class="d-flex align-center info-item">
                    <span class="mr-4 font-weight-medium nowrap" v-text="`${$t('roles.guide')}:`" />
                    <v-select
                        v-if="isEditing.guide"
                        v-model="guideId"
                        :items="guidesList"
                        :placeholder="$t('add_guide')"
                        :loading="isLoading.guide"
                        clearable
                        :disabled="isLoading.guide"
                        item-value="personId"
                        item-title="fullName"
                        density="compact"
                        class="mt-0 mr-2 info-input"
                        hide-details
                    />
                    <span
                        v-else
                        class="mr-2"
                        v-text="guide ? `${guide?.firstName || ''} ${guide?.lastName || ''}` : $t('add_guide')"
                    />
                    <template v-if="isTO1">
                        <v-btn
                            v-if="!isEditing.guide"
                            :loading="isLoading.guide"
                            :disabled="isLoading.guide"
                            color="primary"
                            icon="mdi-pencil"
                            variant="text"
                            size="x-small"
                            @click="startEditing('guide')"
                        />
                        <v-btn
                            v-if="isEditing.guide"
                            :loading="isLoading.guide"
                            :disabled="isLoading.guide"
                            color="error"
                            icon="mdi-close"
                            variant="text"
                            size="x-small"
                            @click="cancelEditing('guide')"
                        />
                        <v-btn
                            v-if="isEditing.guide"
                            :loading="isLoading.guide"
                            :disabled="isLoading.guide"
                            color="success"
                            icon="mdi-check"
                            variant="text"
                            size="x-small"
                            @click="saveDetailsInfo('guide')"
                        />
                    </template>
                </div>
                <div v-if="isTO1 || driver" class="d-flex align-center info-item">
                    <span class="mr-4 font-weight-medium nowrap" v-text="`${$t('roles.driver')}:`" />
                    <v-select
                        v-if="isEditing.driver"
                        v-model="driverId"
                        :items="driversList"
                        :placeholder="$t('add_driver')"
                        :loading="isLoading.driver"
                        :disabled="isLoading.driver"
                        item-value="personId"
                        item-title="fullName"
                        clearable
                        density="compact"
                        class="mt-0 mr-2 info-input"
                        hide-details
                    />
                    <span
                        v-else
                        class="mr-2"
                        v-text="driver ? `${driver.firstName} ${driver.lastName}` : $t('add_driver')"
                    />
                    <template v-if="isTO1">
                        <v-btn
                            v-if="!isEditing.driver"
                            :loading="isLoading.driver"
                            :disabled="isLoading.driver"
                            color="primary"
                            icon="mdi-pencil"
                            variant="text"
                            size="x-small"
                            @click="startEditing('driver')"
                        />
                        <v-btn
                            v-if="isEditing.driver"
                            :loading="isLoading.driver"
                            :disabled="isLoading.driver"
                            color="error"
                            icon="mdi-close"
                            variant="text"
                            size="x-small"
                            @click="cancelEditing('driver')"
                        />
                        <v-btn
                            v-if="isEditing.driver"
                            :loading="isLoading.driver"
                            :disabled="isLoading.driver"
                            color="success"
                            icon="mdi-check"
                            variant="text"
                            size="x-small"
                            @click="saveDetailsInfo('driver')"
                        />
                    </template>
                </div>
            </v-col>
            <v-col cols="12" md="5">
                <div v-if="isShowVehicleType" class="d-flex align-center info-item">
                    <span class="me-4 font-weight-medium nowrap" v-text="`${$t('vehicle_type')}:`" />
                    <v-select
                        v-if="isEditing.vehicleTypeId"
                        v-model="vehicleTypeId"
                        :items="vehicleTypesList"
                        :placeholder="$t('vehicle_type')"
                        :loading="isLoading.vehicleType"
                        :disabled="isLoading.vehicleTypeId"
                        item-title="name"
                        item-value="id"
                        single-line
                        density="compact"
                        class="mt-0 mr-2 info-input"
                        hide-details
                        clearable
                    />
                    <span
                        v-else
                        class="me-2"
                        v-text="
                            selectedGroupDetails?.vehicleTypeId
                                ? `${vehicleType?.name || selectedGroupDetails?.vehicleTypeId}`
                                : $t('add_vehicle_type')
                        "
                    />
                    <template v-if="isTO1">
                        <v-btn
                            v-if="!isEditing.vehicleTypeId"
                            :loading="isLoading.vehicleTypeId"
                            :disabled="isLoading.vehicleTypeId"
                            color="primary"
                            icon="mdi-pencil"
                            variant="text"
                            size="x-small"
                            @click="startEditing('vehicleTypeId')"
                        />
                        <v-btn
                            v-if="isEditing.vehicleTypeId"
                            :loading="isLoading.vehicleTypeId"
                            :disabled="isLoading.vehicleTypeId"
                            color="error"
                            icon="mdi-close"
                            variant="text"
                            size="x-small"
                            @click="cancelEditing('vehicleTypeId')"
                        >
                            <v-icon>mdi-close</v-icon>
                        </v-btn>
                        <v-btn
                            v-if="isEditing.vehicleTypeId"
                            :loading="isLoading.vehicleTypeId"
                            :disabled="isLoading.vehicleTypeId"
                            color="success"
                            icon="mdi-check"
                            variant="text"
                            size="x-small"
                            @click="saveDetailsInfo('vehicleTypeId')"
                        />
                    </template>
                </div>
                <div v-if="selectedGroupDetails?.vehicleTypeId && isShowVehicleType">
                    {{ $t('total_seats') }}: {{ vehicleType?.maxCapacity }} / {{ $t('seats_sold') }}:
                    {{ totalBookedSeats }} / {{ $t('seats_available') }}: {{ availableSeats }}
                </div>
                <div v-if="availableSeats < 0" class="text-subtitle-2 pt-1 text-red-darken-4 font-weight-regular">
                    {{ $t('vehicle_type_selection_warning') }}
                </div>
                <div v-if="isTO1" class="d-flex align-center info-item">
                    <span class="me-4 font-weight-medium nowrap" v-text="`${$t('vehicle')}:`" />
                    <v-text-field
                        v-if="isEditing.vehicle"
                        v-model="vehicle"
                        :placeholder="$t('vehicle')"
                        :loading="isLoading.vehicle"
                        :disabled="isLoading.vehicle"
                        density="compact"
                        class="me-2 info-input"
                        hide-details
                    />
                    <span v-else class="me-2" v-text="vehicle ? `${vehicle}` : $t('add_vehicle')" />
                    <template v-if="isTO1">
                        <v-btn
                            v-if="!isEditing.vehicle"
                            :loading="isLoading.vehicle"
                            :disabled="isLoading.vehicle"
                            color="primary"
                            icon="mdi-pencil"
                            variant="text"
                            size="x-small"
                            @click="startEditing('vehicle')"
                        />
                        <v-btn
                            v-if="isEditing.vehicle"
                            :loading="isLoading.vehicle"
                            :disabled="isLoading.vehicle"
                            color="error"
                            icon="mdi-close"
                            variant="text"
                            size="x-small"
                            @click="cancelEditing('vehicle')"
                        >
                            <v-icon>mdi-close</v-icon>
                        </v-btn>
                        <v-btn
                            v-if="isEditing.vehicle"
                            :loading="isLoading.vehicle"
                            :disabled="isLoading.vehicle"
                            color="success"
                            icon="mdi-check"
                            variant="text"
                            size="x-small"
                            @click="saveDetailsInfo('vehicle')"
                        />
                    </template>
                </div>
            </v-col>
            <v-col v-if="selectedGroupDetails?.vehicleTypeId && isShowVehicleType" cols="12" md="2" class="pt-3 pb-5">
                <div class="text-link" @click="isShowModal = true">
                    {{ $t('view_seat_map') }}
                </div>
                <ModalWrapper
                    v-model="isShowModal"
                    :max-width="seatMap?.schema ? useWidthForSeatMap(vehicleType?.maxCapacity) : 600"
                >
                    <template v-slot:title>
                        {{ $t('seat_map') }}
                    </template>
                    <template v-slot:content>
                        <div v-if="seatMap?.schema">
                            <div class="d-flex">
                                <SeatMap :seat-map="prepareSeatMap" />
                            </div>
                            <div>{{ $t('total_seats') }}: {{ vehicleType?.maxCapacity || '' }}</div>
                            <div>{{ $t('total_booked') }}: {{ totalBookedSeats }}</div>
                            <div
                                v-if="availableSeats < 0"
                                class="text-subtitle-2 pt-1 text-red-darken-4 font-weight-regular"
                            >
                                {{ $t('vehicle_type_selection_warning') }}
                            </div>
                            <div class="pt-3">{{ $t('assigned') }}: {{ assignedSeats }}</div>
                            <div>{{ $t('unassigned') }}: {{ unassignedSeats }}</div>
                        </div>
                        <div v-else class="pb-5">{{ $t('seat_map_not_found') }}</div>
                    </template>
                </ModalWrapper>
            </v-col>
            <v-col v-if="isTO1 || selectedGroupDetails.comment" cols="12" class="pt-3 pb-5">
                <div class="d-flex flex-column flex-md-row align-md-center info-item">
                    <span class="mr-4 font-weight-medium" v-text="`${$t('comments')}:`" />
                    <div v-if="isEditing.comment" class="d-flex align-center">
                        <v-textarea
                            v-model="comment"
                            :loading="isLoading.comment"
                            :disabled="isLoading.comment"
                            :placeholder="$t('add_comment')"
                            rows="1"
                            density="compact"
                            class="mt-0"
                            hide-details
                        />
                        <v-btn
                            v-if="isEditing.comment"
                            :loading="isLoading.comment"
                            :disabled="isLoading.comment"
                            color="error"
                            icon="mdi-close"
                            variant="text"
                            size="x-small"
                            @click="cancelEditing('comment')"
                        />
                        <v-btn
                            v-if="isEditing.comment"
                            :loading="isLoading.comment"
                            :disabled="isLoading.comment"
                            color="success"
                            icon="mdi-check"
                            variant="text"
                            size="x-small"
                            @click="saveDetailsInfo('comment')"
                        />
                    </div>
                    <div v-else>
                        <span class="mr-2" v-text="comment ? comment : $t('add_comment')" />
                        <v-btn
                            v-if="isTO1"
                            :loading="isLoading.comment"
                            :disabled="isLoading.comment"
                            color="primary"
                            icon="mdi-pencil"
                            variant="text"
                            size="x-small"
                            @click="startEditing('comment')"
                        />
                    </div>
                </div>
            </v-col>
        </v-row>
    </div>
</template>

<script lang="ts">
    import {Component, Prop, VModel, Vue, Watch} from 'vue-facing-decorator'
    import {getServiceTypeIcon} from '~src/utils/helpers'
    import DatePicker from '@/components/snippets/forms/DatePicker'
    import TimeField from '~/components/snippets/forms/TimeField.vue'
    import SeatMap from '~/components/SeatMap.vue'

    @Component({
        components: {SeatMap, TimeField, DatePicker},
    })
    export class GroupDetailsInfo extends Vue {
        @VModel() selectedGroupDetails
        @Prop() groupId
        @Prop({default: false, type: Boolean}) isShowSeats

        isEditingComment = false
        isLoading = {
            guide: false,
            driver: false,
            comment: false,
            status: false,
            vehicle: false,
            vehicleTypeId: false,
            dateTime: false,
        }
        isEditing = {
            guide: false,
            driver: false,
            comment: false,
            status: false,
            vehicle: false,
            vehicleTypeId: false,
            dateTime: false,
        }
        status = null
        comment = null
        driverId = null
        guideId = null
        vehicle = null
        vehicleTypeId = null
        dateTime = null
        driversList = []
        vehicleTypesList = []
        guidesList = []
        isShowModal = false
        seatMap = null
        schema = null

        @Watch('selectedGroupDetails.vehicleTypeId', {deep: true})
        async onVehicleTypeChange() {
            this.seatMap = null
            await this.loadSeatMap()
        }

        async mounted() {
            this.comment = this.selectedGroupDetails?.comment ? this.selectedGroupDetails.comment : ''
            this.status = this.selectedGroupDetails?.status ? this.selectedGroupDetails.status : null
            this.driverId = this.selectedGroupDetails?.driver?.personId
                ? this.selectedGroupDetails.driver.personId
                : null
            this.guideId = this.selectedGroupDetails?.guide?.personId ? this.selectedGroupDetails.guide.personId : null
            this.vehicle = this.selectedGroupDetails?.vehicle || null
            this.dateTime = `${this.selectedGroupDetails?.dateTime.split(' ')[0]}T${
                this.selectedGroupDetails?.dateTime.split(' ')[1]
            }`
            this.vehicleTypeId = this.selectedGroupDetails?.vehicleTypeId
                ? this.selectedGroupDetails.vehicleTypeId
                : null

            if (this.isTO1 && this.user.person.companyId) {
                try {
                    const {users} = await this.$api.persons.get({companyId: this.user.person.companyId})

                    this.driversList = users
                        .filter(u => u.role.includes('driver'))
                        .map(u => {
                            u.fullName = [u.firstName, u.lastName].join(' ')
                            return u
                        })
                    this.guidesList = users
                        .filter(u => u.role.includes('guide'))
                        .map(u => {
                            u.fullName = [u.firstName, u.lastName].join(' ')
                            return u
                        })
                    // eslint-disable-next-line no-empty
                } catch (e) {}
            }
            await this.loadVehicleTypes()
            if (this.vehicleTypeId) await this.loadSeatMap()
        }

        get availableSeats() {
            return this.vehicleType?.maxCapacity - this.totalBookedSeats || ''
        }

        get assignedSeats() {
            if (!this.selectedGroupDetails?.guests?.length) return 0
            return this.selectedGroupDetails.guests.filter(guest => guest.seatNumber).length
        }

        get unassignedSeats() {
            if (!this.selectedGroupDetails?.guests?.length) return 0
            return this.selectedGroupDetails.guests.filter(guest => !guest.seatNumber).length
        }

        get totalBookedSeats() {
            return this.selectedGroupDetails?.guests?.length
        }

        async loadVehicleTypes() {
            try {
                this.isLoading.vehicleTypeId = true
                const {vehicleTypes} = await this.$api.vehicleType.get({active: true})
                this.vehicleTypesList = vehicleTypes
            } catch (e) {
                this.vehicleTypesList = []
            } finally {
                this.isLoading.vehicleTypeId = false
            }
        }

        async loadSeatMap() {
            try {
                const {
                    seatMaps: [_seatMaps],
                } = await this.$api.seatMap.get({linkedObjectId: this.selectedGroupDetails?.vehicleTypeId})

                if (_seatMaps) {
                    this.seatMap = _seatMaps
                    this.schema = JSON.parse(this.seatMap.schema)
                }
            } catch (e) {
                this.seatMap = null
            }
        }

        get prepareSeatMap() {
            const newBusLayout = {}
            for (const [row, seats] of Object.entries(this.schema)) {
                newBusLayout[row] = seats.map(seat => {
                    if (seat !== null) {
                        return {
                            seatNumber: seat,
                            occupied: false,
                            available: true,
                            orderId: null,
                            firstName: null,
                            lastName: null,
                        }
                    }
                    return null
                })
            }
            this.selectedGroupDetails.guests.forEach(guest => {
                const seatNumber = guest.seatNumber
                for (const seats of Object.values(newBusLayout)) {
                    seats.forEach(seat => {
                        if (seat && seat.seatNumber === seatNumber) {
                            seat.occupied = true
                            seat.available = false
                            seat.orderId = guest.orderId
                            seat.firstName = guest.firstName
                            seat.lastName = guest.lastName
                        }
                    })
                }
            })
            return newBusLayout
        }

        startEditing(type) {
            this.isEditing[type] = true
            for (const key in this.isEditing) {
                if (key !== type) this.cancelEditing(key)
            }
        }

        cancelEditing(type) {
            this.isEditing[type] = false

            if (type === 'comment') {
                this.comment = this.selectedGroupDetails?.comment ? this.selectedGroupDetails.comment : ''
            } else if (type === 'status') {
                this.status = this.selectedGroupDetails.status ? this.selectedGroupDetails.status : null
            } else if (type === 'guide') {
                this.guideId = this.selectedGroupDetails?.guide?.personId
                    ? this.selectedGroupDetails.guide.personId
                    : null
            } else if (type === 'driver') {
                this.driverId = this.selectedGroupDetails?.driver?.personId
                    ? this.selectedGroupDetails.driver.personId
                    : null
            }
        }

        async saveDetailsInfo(type) {
            this.isLoading[type] = true
            const data = {}

            if (this.comment) data.comment = this.comment
            if (this.status) data.status = this.status
            if (this.guideId) data.guideId = this.guideId
            if (this.driverId) data.driverId = this.driverId
            if (this.vehicle) data.vehicle = this.vehicle
            if (this.dateTime) data.dateTime = this.dateTime
            if (this.vehicleTypeId) data.vehicleTypeId = this.vehicleTypeId

            try {
                await this.$api.modifySchedulerGroup.put(this.groupId, data)

                if (type === 'comment') this.selectedGroupDetails.comment = this.comment
                if (type === 'vehicle') this.selectedGroupDetails.vehicle = this.vehicle
                if (type === 'dateTime') this.selectedGroupDetails.dateTime = this.dateTime
                if (type === 'status') this.selectedGroupDetails.status = this.status
                if (type === 'driver') {
                    this.selectedGroupDetails.driver = this.driversList.find(
                        driver => driver.personId === this.driverId
                    )
                }
                if (type === 'guide')
                    this.selectedGroupDetails.guide = this.guidesList.find(guide => guide.personId === this.guideId)
                if (type === 'vehicleTypeId') this.selectedGroupDetails.vehicleTypeId = this.vehicleTypeId
            } catch (e) {
                this.cancelEditing(type)
                this.$toast.error(this.$t('error_message.save_error'))
            } finally {
                this.isLoading[type] = false
                this.isEditing[type] = false
            }
        }

        get serviceTypeIcon() {
            return getServiceTypeIcon(this.selectedGroupDetails)
        }

        get formattedDate() {
            return this.$filters.dateTimeFormat(this.$dateFns.parseISO(this.selectedGroupDetails.dateTime))
        }

        get guide() {
            return this.selectedGroupDetails?.guide
        }

        get driver() {
            return this.selectedGroupDetails?.driver
        }

        get vehicleType() {
            if (!this.vehicleTypesList?.length || !this.selectedGroupDetails?.vehicleTypeId) return null
            return this.vehicleTypesList?.find(({id}) => id === this.selectedGroupDetails?.vehicleTypeId)
        }

        get isShowVehicleType() {
            return this.isShowSeats
        }

        get statusesList() {
            const statusesList = ['NEW', 'IN_PROGRESS', 'DONE', 'CANCELLED']

            return statusesList.map(value => ({value, text: this.$t(`orderStatus.${value}`)}))
        }

        get orderStatus() {
            if (!this.status) return null

            return this.$t(`order_statuses.${this.status.toUpperCase().replace(/[\s.]/g, '_')}`)
        }

        get statusColor() {
            return runtimeStore.orderStatusColor(this.selectedGroupDetails.status)
        }

        get isTO1() {
            return authStore.isTO1
        }

        get user() {
            return authStore.user
        }

        get startDate() {
            return this.dateTime.split('T')[0]
        }

        set startDate(date) {
            this.dateTime = `${date}T${this.dateTime.split('T')[1]}`
        }

        get startTime() {
            return this.dateTime.split('T')[1]
        }

        set startTime(time) {
            this.dateTime = `${this.dateTime.split('T')[0]}T${time}`
        }
    }

    export default GroupDetailsInfo
</script>

<style scoped>
    .info-item {
        min-height: 44px;
    }
    .info-input {
        max-width: 300px;
    }
</style>
